import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthUser } from '../../../utils/auth.util';
import {
  fetchTicketByTicketNo,
  fetchTicketResponseListing,
  downloadTicketAttachment,
  deleteTicketResponse,
  updateTicket,
  cancelMyTicket,
} from '../../../store/apis/ticketService';
import { fetchHRCodeList } from '../../../store/apis/commonService';
import { ROLE_NAMES } from '../../../common/constant';

const TicketDetailsContext = createContext({
  ticketDetails: null,
  roleType: null,
  isLoading: false,
  error: null,
});

export function useTicketDetails() {
  const ctx = useContext(TicketDetailsContext);
  if (!ctx) {
    throw new Error(
      'useTicketDetails must be used within TicketDetailsProvider',
    );
  }
  return ctx;
}

export function TicketDetailsProvider({ children }) {
  const queryClient = useQueryClient();
  const { id: ticketId } = useParams();
  const [roleType, setRoleType] = useState(null);
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);

  const TICKET_ROLE_TYPES = useMemo(
    () => ({
      HR: 'hr',
      HR_MANAGER: 'hrManager',
      CREATED_BY: 'createdBy',
      ASSIGNED_TO: 'assignedTo',
      OTHER: 'other',
    }),
    [],
  );

  function determineRole(ticket, user) {
    if (!ticket) return null;
    if (ticket?.RequestorCd.toLowerCase() === user?.employeeId.toLowerCase()) {
      return TICKET_ROLE_TYPES.CREATED_BY;
    }
    if (ticket.AssignedToId === user.id) return TICKET_ROLE_TYPES.ASSIGNED_TO;
    return TICKET_ROLE_TYPES.OTHER;
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['ticketDetails', ticketId],
    queryFn: () =>
      fetchTicketByTicketNo({ ticketNo: ticketId }).then(res => res[0] || null),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    enabled: !!ticketId,
  });

  const {
    data: ticketResponseListing,
    isLoading: ticketResponseListingLoading,
  } = useQuery({
    queryKey: ['ticketResponseListing', ticketId],
    queryFn: async () => {
      const ticketResponseData = await fetchTicketResponseListing({
        ticketNo: ticketId,
      });
      return ticketResponseData || [];
    },
    enabled: !!ticketId,
  });

  const downloadAttachmentMutation = useMutation({
    mutationFn: payload => downloadTicketAttachment(payload),
  });

  const updateTicketMutation = useMutation({
    mutationFn: payload => updateTicket(payload),
  });

  const cancelMyTicketMutation = useMutation({
    mutationFn: payload => cancelMyTicket(payload),
  });

  const { data: ticketStatusData } = useQuery({
    queryKey: ['ticketStatusData'],
    queryFn: () =>
      fetchHRCodeList({
        type: 'TICKET_STATUS',
      }),
    enabled: true,
  });

  useEffect(() => {
    if (!data) return;
    const user = getAuthUser();
    setRoleType(determineRole(data, user));
  }, [data, getAuthUser]);

  const deleteTicketResponseMutation = useMutation({
    mutationFn: payload => deleteTicketResponse(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ticketResponseListing', ticketId],
      });
    },
  });

  const checkIfHR = React.useCallback(() => {
    const user = getAuthUser();
    return user?.role?.Name === ROLE_NAMES.HR;
  }, []);

  const value = useMemo(
    () => ({
      ticketDetails: data,
      roleType,
      isLoading,
      error,
      ticketResponseListing,
      downloadAttachmentMutation,
      displayModalOpen,
      setDisplayModalOpen,
      displayModalFile,
      setDisplayModalFile,
      deleteTicketResponseMutation,
      TICKET_ROLE_TYPES,
      checkIfHR,
      updateTicketMutation,
      cancelMyTicketMutation,
      ticketStatusData,
      ticketResponseListingLoading,
    }),
    [
      data,
      roleType,
      isLoading,
      error,
      ticketResponseListing,
      downloadAttachmentMutation,
      displayModalOpen,
      setDisplayModalOpen,
      displayModalFile,
      setDisplayModalFile,
      deleteTicketResponseMutation,
      TICKET_ROLE_TYPES,
      checkIfHR,
      updateTicketMutation,
      cancelMyTicketMutation,
      ticketStatusData,
      ticketResponseListingLoading,
    ],
  );

  return (
    <TicketDetailsContext.Provider value={value}>
      {children}
    </TicketDetailsContext.Provider>
  );
}
